import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import React, { useMemo } from 'react';
import { ISbStoryData } from 'storyblok';

import { WORK_PAGE_TYPE } from '../../../constants';
import {
  VARIANT_COLOURS,
  VARIANT_SIZES_SORT_ORDER,
} from '../../../constants/variants';
import { VariantOption } from '../../../types/Analytics';
import {
  ArtistStoryblok,
  ProductV2Storyblok,
} from '../../../types/generated-storyblok';
import type { VariantOptions } from '../../../types/Products';
import type { ProductPurchaseDetails } from '../../../types/Purchasing';
import { TrackProductVariantSwitchClicked } from '../../../utils/analytics';
import { renderArtistsNamesFromPageContent } from '../../../utils/artists';
import {
  getLocalisedProductUrl,
  getVariantOptions,
} from '../../../utils/variants';
import { VariantIcon } from '../VariantIcon';
import type { ProductDetailsProps } from './types';

type ProductVariantGroupProps = {
  product: ProductType;
  variantOptions: VariantOptions;
  slug?: string;
};

const trackVariantOption = (
  product: ProductType,
  variant: VariantProduct,
  idx: number,
  variantOption: string,
) => {
  TrackProductVariantSwitchClicked({
    page: WORK_PAGE_TYPE,
    context: 'variant switch',
    variantOption: variantOption as VariantOption,
    position: idx,
    sourceProduct: {
      id: product.id[0].id,
      artistName: renderArtistsNamesFromPageContent(product.productArtist),
      editionSize: product.editionSize ? parseInt(product.editionSize, 10) : 0,
      releaseType: product.releaseType,
      title: product.title,
      type: 'Product',
      medium: product.medium ? product.medium[0] : undefined,
      releaseDate: product.launchDate,
      pricesAndCurrencies: product.pricesAndAvailability?.pricesAndCurrencies,
    },
    targetProduct: {
      id:
        variant.content.component === 'product_v2'
          ? (variant.content as unknown as ProductV2Storyblok).shopifyId
          : variant.content.id[0].id,
      artistName:
        variant.content.component === 'product_v2'
          ? (
              (variant.content as unknown as ProductV2Storyblok).artists[0]
                .artist as ISbStoryData<ArtistStoryblok>
            )?.name
          : (variant.content.productArtist[0] as ResolvedRelatedArtist).artist
              .name,
      editionSize: variant.content.editionSize
        ? parseInt(variant.content.editionSize, 10)
        : 0,
      releaseType: variant.content.releaseType,
      title: variant.content.title,
      type: 'Product',
      medium: variant.content.medium ? variant.content.medium[0] : undefined,
      releaseDate: variant.content.launchDate,
      pricesAndCurrencies:
        variant.content.pricesAndAvailability?.pricesAndCurrencies,
    },
  });
};

export const ProductVariantGroup: React.FC<React.PropsWithChildren> = ({
  children,
}) => (
  <div className="flex flex-wrap items-center justify-center gap-3">
    {children}
  </div>
);

const ProductVariantGroup_Sizes: React.FC<ProductVariantGroupProps> = ({
  product,
  slug,
  variantOptions,
}) => {
  if (variantOptions.sizes.length === 0) return null;

  const { i18n, t } = useTranslation('product');
  const sizes = variantOptions.sizes.sort(
    (a, b) =>
      VARIANT_SIZES_SORT_ORDER[a.size.size] -
      VARIANT_SIZES_SORT_ORDER[b.size.size],
  );

  return (
    <ProductVariantGroup>
      {sizes.map(({ size, variant }, idx) => (
        <Link
          key={idx}
          href={getLocalisedProductUrl(variant.slug, i18n.language)}
          onClick={() => {
            trackVariantOption(product, variant, idx, 'size');
          }}
        >
          <VariantIcon
            active={variant.slug === slug}
            text={size.size.toUpperCase()}
            tooltipText={t(`variants.sizes.${size.size}`)}
          />
        </Link>
      ))}
    </ProductVariantGroup>
  );
};

const ProductVariantGroup_Colourways: React.FC<ProductVariantGroupProps> = ({
  product,
  slug,
  variantOptions,
}) => {
  if (variantOptions.colourways.length === 0) return null;

  const { i18n } = useTranslation();
  const colourways = variantOptions.colourways.sort((a, b) =>
    a.variant.slug.localeCompare(b.variant.slug),
  );

  return (
    <ProductVariantGroup>
      {colourways.map(({ colourway, variant }, idx) => (
        <Link
          key={idx}
          href={getLocalisedProductUrl(variant.slug, i18n.language)}
          onClick={() => {
            trackVariantOption(product, variant, idx, 'colour');
          }}
        >
          <VariantIcon
            active={variant.slug === slug}
            backgroundColours={colourway.colours.map((c) => VARIANT_COLOURS[c])}
            tooltipText={colourway.name}
          />
        </Link>
      ))}
    </ProductVariantGroup>
  );
};

const ProductVariantGroup_Finishing: React.FC<ProductVariantGroupProps> = ({
  product,
  slug,
  variantOptions,
}) => {
  if (variantOptions.finishing.length <= 1) return null;

  const { i18n, t } = useTranslation('product');
  const finishings = variantOptions.finishing.sort((a, b) =>
    a.variant.slug.localeCompare(b.variant.slug),
  );

  return (
    <ProductVariantGroup>
      {finishings.map(({ handFinished, variant }, idx) => (
        <Link
          key={idx}
          href={getLocalisedProductUrl(variant.slug, i18n.language)}
          onClick={() => {
            trackVariantOption(product, variant, idx, 'finishing');
          }}
        >
          <VariantIcon
            active={variant.slug === slug}
            crossedOut={!handFinished}
            text="HF"
            tooltipText={
              handFinished
                ? t('variants.handFinishedYes')
                : t('variants.handFinishedNo')
            }
          />
        </Link>
      ))}
    </ProductVariantGroup>
  );
};

const ProductVariantGroup_Compositions: React.FC<ProductVariantGroupProps> = ({
  product,
  slug,
  variantOptions,
}) => {
  if (variantOptions.compositions.length === 0) return null;

  const { i18n, t } = useTranslation('product');
  const options = variantOptions.compositions.sort((a, b) => {
    return (
      Number(a.composition.compositionNumber) -
      Number(b.composition.compositionNumber)
    );
  });

  return (
    <ProductVariantGroup>
      {options.map(({ composition, variant }, idx) => {
        const firstAsset = variant.content.images?.[0];
        const isVideo = firstAsset?.component === 'Video';
        const thumbnail = isVideo
          ? firstAsset?.coverImage?.filename
          : firstAsset?.image?.filename;

        return (
          <Link
            key={idx}
            href={getLocalisedProductUrl(variant.slug, i18n.language)}
            onClick={() => {
              trackVariantOption(product, variant, idx, 'composition');
            }}
          >
            <VariantIcon
              active={variant.slug === slug}
              thumbnail={thumbnail}
              text={
                !thumbnail ? `#${composition.compositionNumber}` : undefined
              }
              tooltipText={t('variants.compositionNumber', {
                number: composition.compositionNumber,
              })}
            />
          </Link>
        );
      })}
    </ProductVariantGroup>
  );
};

const ProductVariantGroup_Dimensions: React.FC<ProductVariantGroupProps> = ({
  product,
  slug,
  variantOptions,
}) => {
  if (variantOptions.dimensions.length === 0) return null;

  const { i18n } = useTranslation();
  const options = variantOptions.dimensions.sort((a, b) =>
    a.dimension.dimension.localeCompare(b.dimension.dimension),
  );

  return (
    <ProductVariantGroup>
      {options.map(({ dimension, variant }, idx) => (
        <Link
          key={idx}
          href={getLocalisedProductUrl(variant.slug, i18n.language)}
          onClick={() => {
            trackVariantOption(product, variant, idx, 'dimension');
          }}
        >
          <VariantIcon
            active={variant.slug === slug}
            text={dimension.dimension}
            tooltipText={dimension.name}
          />
        </Link>
      ))}
    </ProductVariantGroup>
  );
};

export const ProductVariants = <T extends ProductPurchaseDetails>({
  product,
  purchasing,
}: Omit<ProductDetailsProps<T>, 'onShowTaxDetails' | 'onShowFramingDetails'> &
  Partial<
    Pick<ProductDetailsProps<T>, 'onShowTaxDetails' | 'onShowFramingDetails'>
  >) => {
  const variants = product.productVariants?.[0]?.variants;

  if (!product.id || !variants) {
    return null;
  }

  const slug = purchasing.product?.slug;
  const variantOptions = useMemo(() => {
    return getVariantOptions(product);
  }, [product]);

  const displayProps: ProductVariantGroupProps = {
    product,
    slug,
    variantOptions,
  };

  const { t } = useTranslation('product');
  return (
    <div className="flex w-full items-center gap-3 rounded-sm border border-divider/20 bg-white p-3">
      <span className="shrink-0">{t('variants.artwork')}</span>
      <div className="flex w-full flex-wrap justify-end gap-3">
        <ProductVariantGroup_Sizes {...displayProps} />
        <ProductVariantGroup_Colourways {...displayProps} />
        <ProductVariantGroup_Finishing {...displayProps} />
        <ProductVariantGroup_Compositions {...displayProps} />
        <ProductVariantGroup_Dimensions {...displayProps} />
      </div>
    </div>
  );
};

export default ProductVariants;
